import React, { useState, useRef, useEffect } from "react";

import { useTranslation } from "react-i18next";
import LazyImage from "../Shared/LazyImage";
import BaseModal from "./BaseModal";
import { toCamelCase } from "../../utils/Helpers";
import { METADATA_COLUMNS, ROLES } from "../../utils/constants";
import scansApi from "../../API/scans.api";
import { isEmpty, isEqual } from "lodash";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import ImageModal from "./imageModal";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";
import SimpleNotification from "../Notifications/SimpleNotification";
import ValidationError from "../Shared/ValidationError";
import InformationTooltip from "../Tooltips/InformationTooltip";
import ScanDetailsModalBody from "./ScanDetailsModalBody";
import { getNumberOfDaysBetweenDates } from "../../utils/DateHelper";
import lookupsAPI from "../../API/lookups.api";

const ScanDetailsModal = ({
    isVisible,
    setIsVisible,
    scanId,
    fields,
    showSpectralData,
    entityName,
    setMixedData,
    gridApi,
    cancelSelection,
    editMode,
    setEditMode,
    setOrganizationUsersLookup,
}) => {
    const [data, setData] = useState({});
    const [referenceData, setReferenceData] = useState({});
    const [showImage, setShowImage] = useState(false);
    const isImageOpened = useRef(false);
    const [loading, setLoading] = useState(false);
    const [saveModal, setSaveModal] = useState(false);
    const [isSampleNameEditable, setIsSampleNameEditable] = useState(false);
    const [reSyncLoading, setReSyncLoading] = useState(false);
    const [usersDictionary, setUsersDictionary] = useState({});
    const { t } = useTranslation();

    useEffect(() => {
        if (scanId) fetchScan();
    }, [scanId]);

    useEffect(() => {
        if (!isEmpty(data)) {
            getIsSampleNameEditable();
        }
    }, [data]);

    useEffect(() => {
        if (isVisible) {
            fetchScan();
        } else setEditMode(false);
    }, [isVisible]);

    const fetchScan = async () => {
        if (scanId) {
            setLoading(true);
            const result = await scansApi.getScanDetailsById(scanId);
            let scanDetailsData = { ...result };
            if (result.createdBy.value === result.collectedBy.value)
                scanDetailsData = { ...scanDetailsData, createdBy: null };
            setData(scanDetailsData);
            fetchOrganizationUsers(result.organizationId, scanDetailsData);
            setReferenceData(scanDetailsData);
            setLoading(false);
        }
    };

    const fetchOrganizationUsers = async (organizationId, scanDetailsData) => {
        let result = [];
        if (usersDictionary[organizationId])
            result = usersDictionary[organizationId];
        else {
            result = await lookupsAPI.getSuperiorUsers(organizationId, true);
            setUsersDictionary((prevData) => ({
                ...prevData,
                [organizationId]: result,
            }));
        }

        const userLookup = [
            { label: t("none"), value: scanDetailsData?.collectedBy?.value },
            ...result.filter(
                (elem) => elem.value !== scanDetailsData?.collectedBy?.value
            ),
        ];

        setOrganizationUsersLookup(userLookup);
    };

    const openImage = () => {
        setShowImage(true);
        isImageOpened.current = true;
    };

    const inputChange = (e, key = "") => {
        if (e.target.name === "referenceValue") {
            setData((prev) => ({
                ...prev,
                predictions: {
                    ...prev.predictions,
                    [key]: {
                        ...prev.predictions[key],
                        [e.target.name]:
                            Number(e.target.value) > 0
                                ? Number(e.target.value)
                                : 0,
                    },
                },
            }));
        } else
            setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const updateScan = async ({ sync = false, setLoader = () => {} } = {}) => {
        setLoader(true);
        const requestBody = {
            scanId,
            name: data.name,
            image: data.image,
            longitude: data.longitude,
            latitude: data.latitude,
            note: data.note,
            supplierId: data?.supplier?.value || null,
            createdById: data.createdBy?.value,
            lotId: data?.lot?.value || null,
            predictions: Object.values(data.predictions)?.map((elem) => ({
                id: elem?.id,
                referenceValue: elem?.referenceValue,
            })),
        };

        let syncScan;
        if (sync) {
            syncScan = await scansApi.syncScans([scanId]);
        }
        const updateResult = await scansApi.editScan(requestBody);
        if (sync && updateResult?.modified) {
            setSaveModal(false);
            setEditMode(false);
            if (syncScan !== 0) SuccessAlert(t("scanSyncAndSaveMessage"));
            else ErrorAlert(t("scanSyncFailMessage"));
        } else if (updateResult?.modified) {
            setEditMode(false);
            SuccessAlert(t("scanUpdateMessage"));
        }
        afterEditScanDetails();
        fetchScan();
        setLoader(false);
    };

    const syncScans = async () => {
        setReSyncLoading(true);
        const syncScan = await scansApi.syncScans([scanId]);
        if (syncScan !== 0) SuccessAlert(t("scanSyncAndSaveMessage"));
        else {
            ErrorAlert(t("scanSyncFailMessage"));
        }
        setReSyncLoading(false);
    };

    const afterDeleteReadings = (result, scanId) => {
        const averageSpectra = result.data?.averageSpectra;
        if (averageSpectra?.length === 0) {
            setMixedData((prevState) => {
                return prevState.filter((item) => {
                    if (item[item.length - 1] !== scanId) return item;
                });
            });
            setIsVisible(false);
            cancelSelection();
        } else {
            setMixedData((prevState) => {
                return prevState.map((item) => {
                    if (item[item.length - 1] === scanId) {
                        item.splice(
                            Object.keys(METADATA_COLUMNS).length + 1,
                            averageSpectra?.length,
                            ...averageSpectra
                        );
                        if (gridApi) {
                            const node = gridApi.getRowNode(scanId);
                            node?.data?.splice(
                                Object.keys(METADATA_COLUMNS).length + 1,
                                averageSpectra?.length,
                                ...averageSpectra
                            );
                            gridApi.redrawRows({ rowNodes: [node] });
                        }
                        return item;
                    } else return item;
                });
            });
        }
    };

    const afterEditScanDetails = () => {
        const node = gridApi.getRowNode(scanId);
        setMixedData((prevState) => {
            prevState[node.rowIndex][0] = data.name;
            prevState[node.rowIndex][4] =
                referenceData.collectedBy?.value === data.createdBy?.value
                    ? "--"
                    : data.createdBy?.label;
            prevState[node.rowIndex][5] = data.lot?.label || "--";
            return prevState;
        });
        node.data[0] = data.name;
        node.data[4] =
            referenceData.collectedBy?.value === data.createdBy?.value
                ? "--"
                : data.createdBy?.label;
        node.data[5] = data.lot?.label || "--";
        gridApi.redrawRows({ rowNodes: [node] });
    };

    const submitSave = () => {
        if (!editMode) {
            setEditMode(true);
        } else if (isEqual(referenceData, data)) {
            setEditMode(false);
        } else if (data.integrated != undefined) {
            setSaveModal(true);
        } else {
            updateScan();
        }
    };

    const syncButtonContent = () => {
        if (reSyncLoading) return t("syncing");
        else if (data?.integrated) return t("reSync");
        else return t("sync");
    };

    const renderSyncButton = () => {
        if (
            data.integrated != undefined &&
            isAuthorized(
                [ROLES.partnerAdmin, ROLES.support, ROLES.admin],
                getRoles()
            )
        )
            return (
                <button
                    onClick={() => syncScans()}
                    disabled={reSyncLoading}
                    className={`${
                        data?.integrated
                            ? "default-green-btn"
                            : "default-orange-btn"
                    } flex gap-1.5 items-center ${
                        reSyncLoading ? "disabled" : ""
                    }`}
                >
                    {reSyncLoading ? (
                        <span className="animate-spin">
                            <i className="fas fa-sync"></i>
                        </span>
                    ) : (
                        <i className="fa fa-sync"></i>
                    )}
                    {syncButtonContent()}
                </button>
            );
    };

    const getIsSampleNameEditable = () => {
        setIsSampleNameEditable(
            data?.createdAt &&
                getNumberOfDaysBetweenDates(
                    new Date(),
                    new Date(data?.createdAt)
                ) <= 30
        );
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                title={`${t(toCamelCase(entityName))} ${t("details")}`}
                titleIcon="fas fa-info-circle"
                contentStyles={{ padding: 0 }}
            >
                <LoadingOverlay active={loading}>
                    <div className="sticky top-0 z-100 shadow bg-white pb-0.2 m-header">
                        <div
                            className={`${
                                !editMode && "mb-5"
                            } px-5 py-3 shadow flex justify-between items-end`}
                        >
                            <div className="flex w-full">
                                <div onClick={openImage}>
                                    <LazyImage
                                        src={
                                            data.image ||
                                            require("../../assets/images/Group 709.png")
                                                .default
                                        }
                                        className="uploaded-img modal-btn-open"
                                        data-modal-window="uploaded-img-box"
                                    />
                                </div>
                                <div className="img-info ml-5 w-1/2">
                                    <div className="input-group mb-0 flex gap-2">
                                        <label className="uppercase mb-0">
                                            {t("sampleName")}
                                        </label>
                                        {data?.outlier && (
                                            <InformationTooltip
                                                message={t("outlierFlag")}
                                                type="error"
                                                appendTo=".m-header"
                                            />
                                        )}
                                    </div>
                                    {editMode ? (
                                        <>
                                            <span className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    className={`input-element ${
                                                        isSampleNameEditable
                                                            ? ""
                                                            : "disabled"
                                                    }`}
                                                    name="name"
                                                    value={data.name}
                                                    onChange={inputChange}
                                                    disabled={
                                                        !isSampleNameEditable
                                                    }
                                                />
                                                {!isSampleNameEditable && (
                                                    <InformationTooltip
                                                        message={t(
                                                            "CantEditSampleNameMessage"
                                                        )}
                                                        type="warn"
                                                        appendTo=".m-header"
                                                    />
                                                )}
                                            </span>

                                            {isEmpty(data.name) && (
                                                <ValidationError
                                                    errorMessage={
                                                        "Sample name connot be empty"
                                                    }
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-sm">
                                            {data.name}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-2 self-center">
                                {renderSyncButton()}
                                <button
                                    onClick={submitSave}
                                    className={`${
                                        editMode
                                            ? "default-orange-outline-btn"
                                            : "default-orange-btn"
                                    } ${
                                        isEmpty(data.name) && "disabled"
                                    } flex gap-1.5 items-center`}
                                    disabled={isEmpty(data.name)}
                                >
                                    <i
                                        className={`fa fa-${
                                            editMode ? "save" : "edit"
                                        }`}
                                    ></i>
                                    {editMode ? t("save") : t("Edit")}
                                </button>
                            </div>
                        </div>
                        {editMode && (
                            <SimpleNotification
                                message={
                                    <span className="flex items-center gap-2 pl-2">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <span>{t("scanEditMessage")}</span>
                                    </span>
                                }
                            />
                        )}
                    </div>
                    <ScanDetailsModalBody
                        data={data}
                        setData={setData}
                        fields={fields}
                        afterDeleteReadings={afterDeleteReadings}
                        showSpectralData={showSpectralData}
                        saveModal={saveModal}
                        setSaveModal={setSaveModal}
                        inputChange={inputChange}
                        editMode={editMode}
                        scanId={scanId}
                        updateScan={updateScan}
                    />
                    {/* IMAGE MODAL */}
                    <ImageModal
                        image={data.image}
                        showImage={showImage}
                        closeImage={setShowImage}
                    />
                </LoadingOverlay>
            </BaseModal>
        </>
    );
};

export default ScanDetailsModal;
